import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Notify } from 'quasar'
import type { NotificationDto, NotificationFeedResponse } from '@roadfootage/api-client'

import logger from '../../logger'
import { useApi } from '../../composables/api'
import {
  createPollScheduler,
  type PollOutcome,
  type PollScheduler
} from '../../utils/poll-scheduler'

const DEFAULT_POLL_SECONDS = 30
const DEFAULT_MAX_POLL_SECONDS = 300
const TOAST_MS = 6000

function sortNewestFirst(a: NotificationDto, b: NotificationDto): number {
  const byTime = (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)
  return byTime !== 0 ? byTime : (b.id ?? '').localeCompare(a.id ?? '')
}

function mergeById(existing: NotificationDto[], incoming: NotificationDto[]): NotificationDto[] {
  const byId = new Map(existing.map((n) => [n.id, n]))
  for (const n of incoming) byId.set(n.id, n)
  return [...byId.values()].sort(sortNewestFirst)
}

function isUnauthorised(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 401
}

/**
 * In-app notifications: the bell, the list and toasts for new items. Background polls use `since` with the server clock from
 * the previous response, so each poll carries only what is new. Failures are logged, never toasted.
 */
export const useNotificationsStore = defineStore('notifications', () => {
  const { meApi } = useApi({ notifyOnError: false })

  const items = ref<NotificationDto[]>([])
  const unreadCount = ref(0)
  const hasMore = ref(false)
  const isLoadingOlder = ref(false)
  const pollAfterSeconds = ref(DEFAULT_POLL_SECONDS)
  const maxPollAfterSeconds = ref(DEFAULT_MAX_POLL_SECONDS)

  let cursor: Date | null = null
  // Bumped by reset(). A response that was requested before a reset belongs to the previous user and is dropped.
  let generation = 0
  let scheduler: PollScheduler | null = null

  function applyServerSettings(response: NotificationFeedResponse) {
    if (response.pollAfterSeconds) pollAfterSeconds.value = response.pollAfterSeconds
    if (response.maxPollAfterSeconds) maxPollAfterSeconds.value = response.maxPollAfterSeconds
    if (response.serverTime) cursor = response.serverTime
  }

  function toastFor(fresh: NotificationDto[]) {
    if (fresh.length === 0) return
    const message =
      fresh.length === 1
        ? fresh[0].body ?? fresh[0].title ?? ''
        : `${fresh.length} new notifications`
    Notify.create({
      type: 'info',
      icon: 'mdi-bell-outline',
      message,
      position: 'bottom',
      timeout: TOAST_MS,
      actions: [
        { icon: 'mdi-close', color: 'white', round: true, dense: true, 'aria-label': 'Dismiss' }
      ]
    })
  }

  /** One poll. The first one loads the newest page; later ones ask only for items since the last response. */
  async function pollOnce(): Promise<PollOutcome> {
    const isFirstLoad = cursor === null
    const requestedIn = generation
    let response: NotificationFeedResponse
    try {
      response = await meApi.meGetNotifications(isFirstLoad ? {} : { since: cursor! })
    } catch (error) {
      if (isUnauthorised(error)) return 'stop'
      logger.error('notifications poll failed', { error })
      return 'idle'
    }

    if (requestedIn !== generation) return 'idle'

    applyServerSettings(response)
    unreadCount.value = response.unreadCount ?? 0

    const incoming = response.items ?? []
    if (isFirstLoad) {
      items.value = mergeById([], incoming)
      hasMore.value = response.hasMore ?? false
      return 'idle'
    }

    const known = new Set(items.value.map((n) => n.id))
    const fresh = incoming.filter((n) => !known.has(n.id))
    items.value = mergeById(items.value, incoming)

    const freshUnread = fresh.filter((n) => !n.readAt)
    toastFor(freshUnread)
    return fresh.length > 0 ? 'new' : 'idle'
  }

  function start() {
    if (scheduler?.isRunning) return
    scheduler = createPollScheduler({
      poll: pollOnce,
      baseSeconds: () => pollAfterSeconds.value,
      maxSeconds: () => maxPollAfterSeconds.value
    })
    scheduler.start()
  }

  function stop() {
    scheduler?.stop()
    scheduler = null
  }

  /** Stops polling and forgets everything, for sign out and account switches. */
  function reset() {
    stop()
    items.value = []
    unreadCount.value = 0
    hasMore.value = false
    isLoadingOlder.value = false
    pollAfterSeconds.value = DEFAULT_POLL_SECONDS
    maxPollAfterSeconds.value = DEFAULT_MAX_POLL_SECONDS
    cursor = null
    generation++
  }

  /** Poll now, for example when the list is opened. */
  function refresh() {
    scheduler?.pollNow()
  }

  async function loadOlder() {
    const oldest = items.value[items.value.length - 1]
    if (!oldest?.createdAt || isLoadingOlder.value) return

    const requestedIn = generation
    isLoadingOlder.value = true
    try {
      const response = await meApi.meGetNotifications({ before: oldest.createdAt })
      if (requestedIn !== generation) return
      items.value = mergeById(items.value, response.items ?? [])
      hasMore.value = response.hasMore ?? false
    } catch (error) {
      logger.error('loading older notifications failed', { error })
    } finally {
      isLoadingOlder.value = false
    }
  }

  async function markRead(id: string) {
    const target = items.value.find((n) => n.id === id)
    if (!target || target.readAt) return

    target.readAt = new Date()
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    try {
      await meApi.meMarkNotificationReadById({ id })
    } catch (error) {
      logger.error('marking a notification read failed', { error })
      target.readAt = null
      unreadCount.value += 1
    }
  }

  async function markAllRead() {
    const previousUnread = unreadCount.value
    const unreadIds = items.value.filter((n) => !n.readAt).map((n) => n.id)
    const now = new Date()
    for (const n of items.value) n.readAt ??= now
    unreadCount.value = 0

    try {
      await meApi.meMarkAllNotificationsRead()
    } catch (error) {
      logger.error('marking all notifications read failed', { error })
      for (const n of items.value) if (unreadIds.includes(n.id)) n.readAt = null
      unreadCount.value = previousUnread
    }
  }

  return {
    items,
    unreadCount,
    hasMore,
    isLoadingOlder,
    pollAfterSeconds,
    maxPollAfterSeconds,
    pollOnce,
    start,
    stop,
    reset,
    refresh,
    loadOlder,
    markRead,
    markAllRead
  }
})
